import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Scissors, Play } from 'lucide-react';

export default function ClipsReview() {
  const navigate = useNavigate();
  const [clips, setClips] = useState([]);
  const [selectedClip, setSelectedClip] = useState(null);
  const [clipName, setClipName] = useState('');
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);

  useEffect(() => {
    fetchPendingClips();
  }, []);

  const fetchPendingClips = async () => {
    try {
      const res = await fetch(`${window.__BACKEND_URL}/api/clips?status=pending`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setClips(data);
      }
    } catch (err) {
      console.error('Failed to fetch clips:', err);
    }
  };

  const handleSelectClip = (clip) => {
    setSelectedClip(clip);
    setClipName(clip.name || '');
    setTrimStart(clip.trim_start || 0);
    setTrimEnd(clip.duration || 0);
  };

  const handleApprove = async () => {
    if (!selectedClip) return;

    try {
      await fetch(`${window.__BACKEND_URL}/api/clips/${selectedClip.id}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: clipName,
          trim_start: trimStart,
          trim_end: trimEnd
        })
      });

      setClips(clips.filter(c => c.id !== selectedClip.id));
      setSelectedClip(null);
    } catch (err) {
      console.error('Failed to approve clip:', err);
    }
  };

  const handleReject = async () => {
    if (!selectedClip) return;

    try {
      await fetch(`${window.__BACKEND_URL}/api/clips/${selectedClip.id}/reject`, {
        method: 'POST',
        credentials: 'include'
      });

      setClips(clips.filter(c => c.id !== selectedClip.id));
      setSelectedClip(null);
    } catch (err) {
      console.error('Failed to reject clip:', err);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">Clips Review</h1>
        <p className="text-[#8A93A6]">Review and approve AI-generated clips</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Pending Clips ({clips.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {clips.map((clip) => (
                  <div
                    key={clip.id}
                    onClick={() => handleSelectClip(clip)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedClip?.id === clip.id
                        ? 'border-pink-500 bg-pink-500/10'
                        : 'border-white/10 hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Play className="h-4 w-4 text-pink-500" />
                      <div className="flex-1">
                        <p className="text-sm text-white">{clip.name || 'Untitled Clip'}</p>
                        <p className="text-xs text-[#8A93A6]">{clip.duration}s</p>
                      </div>
                    </div>
                  </div>
                ))}

                {clips.length === 0 && (
                  <p className="text-sm text-[#8A93A6] text-center py-8">No clips pending review</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2">
          {selectedClip ? (
            <Card className="glass">
              <CardHeader>
                <CardTitle>Review Clip</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="aspect-video bg-black rounded-lg flex items-center justify-center">
                  <Play className="h-16 w-16 text-white/20" />
                </div>

                <div>
                  <label className="text-sm text-[#8A93A6] mb-2 block">Clip Name</label>
                  <Input
                    value={clipName}
                    onChange={(e) => setClipName(e.target.value)}
                    className="bg-[#1C1F26] border-white/10 text-white"
                    placeholder="Enter clip name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-[#8A93A6] mb-2 block">Trim Start (s)</label>
                    <Input
                      type="number"
                      value={trimStart}
                      onChange={(e) => setTrimStart(Number(e.target.value))}
                      className="bg-[#1C1F26] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#8A93A6] mb-2 block">Trim End (s)</label>
                    <Input
                      type="number"
                      value={trimEnd}
                      onChange={(e) => setTrimEnd(Number(e.target.value))}
                      className="bg-[#1C1F26] border-white/10 text-white"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleApprove}
                    className="flex-1 bg-green-500 hover:bg-green-600"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    onClick={handleReject}
                    variant="outline"
                    className="flex-1 border-red-500/30 text-red-500 hover:bg-red-500/10"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="glass">
              <CardContent className="py-16">
                <div className="text-center">
                  <Scissors className="h-16 w-16 text-[#8A93A6] mx-auto mb-4" />
                  <p className="text-[#8A93A6]">Select a clip to review</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
